import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import { applyTenantTheme } from '../lib/tenantTheme';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  // JWT is stored in-memory only — the httpOnly cookie set by the backend handles
  // session persistence across page refreshes. This prevents XSS scripts from
  // reading the token out of sessionStorage / localStorage.
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  // Per-domain UI format: 'supervisor_hub' (Total Fusion) | 'classic'.
  // null = not resolved yet (branding fetch in flight).
  const [uiLayout, setUiLayout] = useState(null);
  // GymSales nav gate — only tenants with an enabled GymSales integration
  // (Total Fusion) see the GymSales Sync menu item.
  const [gymsalesEnabled, setGymsalesEnabled] = useState(false);

  // Masquerade state — when master_admin is viewing as a domain user
  const [masqueradeUser, setMasqueradeUser] = useState(null);
  const [masqueradeToken, setMasqueradeToken] = useState(null);
  const adminTokenRef = useRef(null);  // stores original admin token while masquerading
  const adminUserRef = useRef(null);

  // userIndustry is not sensitive — sessionStorage is acceptable here.
  const [userIndustry, setUserIndustry] = useState(
    sessionStorage.getItem('userIndustry') || null
  );

  // Fetches the current tenant's branding and applies theme CSS vars.
  // Silent on any failure — theming is a nice-to-have, never a blocker.
  const _refreshTenantTheme = useCallback(async (authToken, currentUser) => {
    // ── Master admin lock — the master admin console (AdminLayout) is
    // deliberately locked to the Yabbit palette. If the caller is master
    // admin, wipe every tenant CSS var so nothing leaks in when they
    // masquerade in and back out.
    if (currentUser?.role === 'master_admin' && !currentUser?.domain_id) {
      applyTenantTheme('');
      setUiLayout('classic');
      setGymsalesEnabled(false);
      return;
    }
    try {
      const r = await axios.get(`${API}/tenant/branding`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setUiLayout(r.data?.ui_layout || 'classic');
      setGymsalesEnabled(Boolean(r.data?.gymsales_enabled));
      applyTenantTheme(
        r.data?.accent_color || '',
        r.data?.logo_url || '',
        r.data?.icon_url || '',
        {
          fontFamilyDisplay: r.data?.font_family_display || '',
          fontFamilyBody: r.data?.font_family_body || '',
          fontUrlDisplay: r.data?.font_url_display || '',
          fontUrlBody: r.data?.font_url_body || '',
          faces: Array.isArray(r.data?.font_faces) ? r.data.font_faces : [],
        },
        {
          bannerUrl: r.data?.banner_url || '',
          bannerColor: r.data?.banner_color || '',
        },
        r.data?.accent_text_color || '',
        r.data?.primary_color || '',
      );
    } catch (e) {
      // Reset to defaults on any failure so a broken branding call
      // doesn't leave stale colours from a previous session lying around.
      applyTenantTheme('');
      setUiLayout('classic');
      setGymsalesEnabled(false);
    }
  }, []);

  const _applyToken = useCallback((newToken) => {
    setToken(newToken);
    if (newToken) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      // Ask the server to clear the httpOnly cookie
      await axios.post(`${API}/auth/logout`);
    } catch (e) {
      // Session is being torn down client-side regardless — the server call
      // is a best-effort cookie clear. Log for observability but do not block.
      console.debug('logout: /auth/logout call failed, continuing with client-side teardown', e?.message);
    }
    _applyToken(null);
    setUser(null);
    setUserIndustry(null);
    setMasqueradeUser(null);
    setMasqueradeToken(null);
    adminTokenRef.current = null;
    adminUserRef.current = null;
    sessionStorage.removeItem('userIndustry');
    applyTenantTheme('');  // clear brand vars on logout
  }, []);  

  // Start masquerading as a domain user (called by admin)
  const startMasquerade = useCallback((targetUser, targetToken) => {
    adminTokenRef.current = token;   // save admin token
    adminUserRef.current = user;     // save admin user
    setMasqueradeUser(targetUser);
    setMasqueradeToken(targetToken);
    _applyToken(targetToken);
    setUser(targetUser);
    _refreshTenantTheme(targetToken, targetUser);  // re-theme to target tenant
  }, [token, user, _refreshTenantTheme]);  

  // Exit masquerade and restore admin session
  const exitMasquerade = useCallback(() => {
    const savedToken = adminTokenRef.current;
    const savedUser = adminUserRef.current;
    setMasqueradeUser(null);
    setMasqueradeToken(null);
    adminTokenRef.current = null;
    adminUserRef.current = null;
    _applyToken(savedToken);
    setUser(savedUser);
    if (savedToken) _refreshTenantTheme(savedToken, savedUser);   // restore admin's own tenant theme
    else applyTenantTheme('');
  }, [_refreshTenantTheme]);  

  // On app load: attempt to restore session from the httpOnly cookie.
  // The /auth/me endpoint validates the cookie and returns a fresh JWT so we can
  // re-populate the in-memory token for Authorization headers on subsequent calls.
  useEffect(() => {
    axios.get(`${API}/auth/me`)
      .then(res => {
        // Support both legacy {User} response and new {user, access_token} shape
        const userData = res.data.user || res.data;
        const freshToken = res.data.access_token || null;
        setUser(userData);
        if (freshToken) _applyToken(freshToken);
        // Fire branding fetch once we know we're authed — don't block
        // the initial paint on it, so it runs in the background.
        if (freshToken) _refreshTenantTheme(freshToken, userData);
      })
      .catch(() => {
        // No valid session — user will need to log in
      })
      .finally(() => setLoading(false));
  }, []);  

  // Global 401 / token-expired interceptor.
  // If any API call returns 401 with "Token expired" (or similar) we silently
  // clear the in-memory session and bounce the user to /login. Without this,
  // the toast would just sit there saying "Token expired" forever while every
  // dashboard request fails one-by-one.
  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const status = error?.response?.status;
        const detail = String(error?.response?.data?.detail || '').toLowerCase();
        const url = String(error?.config?.url || '');
        // Skip the auth endpoints themselves — a 401 on /auth/login means
        // "wrong password", a 401 on /auth/me on initial load just means
        // "not logged in yet". Neither should force a redirect.
        const isAuthEndpoint = url.includes('/auth/login')
          || url.includes('/auth/register')
          || url.includes('/auth/me');
        const isExpired = status === 401 && (
          detail.includes('expired')
          || detail.includes('invalid token')
          || detail.includes('could not validate')
        );
        if (isExpired && !isAuthEndpoint) {
          // Clear in-memory state — same as logout() but without an extra
          // network call (the cookie is dead anyway).
          _applyToken(null);
          setUser(null);
          setUserIndustry(null);
          setMasqueradeUser(null);
          setMasqueradeToken(null);
          adminTokenRef.current = null;
          adminUserRef.current = null;
          sessionStorage.removeItem('userIndustry');
          if (window.location.pathname !== '/login') {
            // Preserve where they were so we can deep-link back after re-login.
            const next = encodeURIComponent(window.location.pathname + window.location.search);
            window.location.replace(`/login?next=${next}&reason=session_expired`);
          }
        }
        return Promise.reject(error);
      },
    );
    return () => axios.interceptors.response.eject(interceptorId);
  }, []);  

  const login = useCallback(async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;

    _applyToken(access_token);
    setUser(userData);
    _refreshTenantTheme(access_token, userData);  // theme the app on login

    if (email.includes('demo.') && email.includes('@yabbit.com')) {
      const industry = email.split('demo.')[1].split('@')[0];
      sessionStorage.setItem('userIndustry', industry);
      setUserIndustry(industry);
    }

    return userData;
  }, [_applyToken, _refreshTenantTheme]);

  const loginWithMagicToken = useCallback(async (magicToken) => {
    const response = await axios.post(`${API}/demo/magic-login`, { token: magicToken });
    const { access_token, user: userData, lead_id } = response.data;
    _applyToken(access_token);
    setUser(userData);
    _refreshTenantTheme(access_token, userData);
    return { ...userData, lead_id };
  }, [_applyToken, _refreshTenantTheme]);

  const register = useCallback(async (email, password, name, role = 'agent') => {
    const response = await axios.post(`${API}/auth/register`, { email, password, name, role });
    const { access_token, user: userData } = response.data;

    _applyToken(access_token);
    setUser(userData);
    _refreshTenantTheme(access_token, userData);

    if (email.includes('demo.') && email.includes('@yabbit.com')) {
      const industry = email.split('demo.')[1].split('@')[0];
      sessionStorage.setItem('userIndustry', industry);
      setUserIndustry(industry);
    }

    return userData;
  }, [_applyToken]);

  // ── Heartbeat: keep `users.last_activity_at` fresh while the tab is
  //    visible so the Admin Activity page reflects idle-but-present users.
  //    5 min cadence gives generous headroom vs the backend's 30s bump
  //    rate-limit without hammering the API.
  useEffect(() => {
    if (!token) return undefined;
    const PING_MS = 5 * 60 * 1000;
    const ping = () => {
      if (document.visibilityState !== 'visible') return;
      // Fire-and-forget — a failed ping means the session expired or the
      // network hiccuped; either way the axios response interceptor above
      // will route to /login on the next real request.
      axios.post(`${API}/auth/ping`).catch(() => {});
    };
    // Kick one immediately so the user shows up "active" the moment they
    // navigate to a long-lived page (call detail, dashboard).
    ping();
    const intervalId = setInterval(ping, PING_MS);
    // Also ping when the tab regains focus (returning from another app).
    const onVis = () => { if (document.visibilityState === 'visible') ping(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [token]);

  // ── Best-effort logout on tab close via navigator.sendBeacon so we
  //    actually get logout events instead of orphaned sessions. Beacons
  //    can't set headers, so we pass the JWT as a query param — the
  //    /auth/logout endpoint accepts token via cookie | Bearer | ?token.
  useEffect(() => {
    if (!token) return undefined;
    const handler = () => {
      try {
        if (!navigator.sendBeacon) return;
        const url = `${API}/auth/logout?reason=tab_close&token=${encodeURIComponent(token)}`;
        // Empty Blob so the beacon isn't rejected for content-length=0.
        navigator.sendBeacon(url, new Blob([], { type: 'text/plain' }));
      } catch {
        /* ignore — closing tab anyway */
      }
    };
    // `pagehide` is more reliable than `beforeunload` on mobile Safari
    // where beforeunload sometimes doesn't fire. Wire both.
    window.addEventListener('pagehide', handler);
    window.addEventListener('beforeunload', handler);
    return () => {
      window.removeEventListener('pagehide', handler);
      window.removeEventListener('beforeunload', handler);
    };
  }, [token]);

  const contextValue = useMemo(() => ({
    user, loading, login, register, logout, token,
    loginWithMagicToken,
    isAuthenticated: !!user,
    userIndustry,
    uiLayout,
    gymsalesEnabled,
    masqueradeUser, masqueradeToken,
    isMasquerading: !!masqueradeUser,
    startMasquerade, exitMasquerade,
  }), [user, loading, login, register, logout, token, userIndustry, uiLayout, gymsalesEnabled, masqueradeUser, masqueradeToken, startMasquerade, exitMasquerade, loginWithMagicToken]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};
